import * as React from 'react';
import { UnlockCircle } from '@bb-ui/icons/dist/small';
import { DefaultButton } from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'hooks/useSnackbar';
import { ReleaseStageMember } from './ReleaseStageMembers.types';
import { ReleaseStageUnlockConfirmationDialog } from './ReleaseStageUnlockConfirmationDialog';
import { useReleaseStageUnlockExecution } from './useReleaseStageUnlockExecution';
import { LockStatusUpdate } from './useReleaseStageMembers';

export interface ReleaseStageUnlockActionsProps {
  releaseStageId: string;
  members: ReleaseStageMember[];
  selectedTenantIds: string[];
  onLockStatusesChange: (updates: LockStatusUpdate[]) => void;
}

export const ReleaseStageUnlockActions: React.FC<ReleaseStageUnlockActionsProps> = ({
  releaseStageId,
  members,
  selectedTenantIds,
  onLockStatusesChange,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { executionReference, targetTenantIds, submitting, error, startUnlock } =
    useReleaseStageUnlockExecution(releaseStageId);
  const [confirmationMembers, setConfirmationMembers] = React.useState<ReleaseStageMember[]>([]);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const optimisticExecutionId = React.useRef<string>();
  const reportedError = React.useRef<Error>();

  const lockedMembers = React.useMemo(
    () => members.filter((member) => member.Locked === true),
    [members],
  );
  const selectedLockedMembers = React.useMemo(() => {
    const selected = new Set(selectedTenantIds);
    return lockedMembers.filter((member) => selected.has(member.TenantId));
  }, [lockedMembers, selectedTenantIds]);

  const openConfirmation = (targets: ReleaseStageMember[]) => {
    setConfirmationMembers(targets);
    setConfirmationOpen(targets.length > 0);
  };

  const confirmUnlock = () => {
    startUnlock(confirmationMembers.map((member) => member.TenantId));
    setConfirmationOpen(false);
  };

  React.useEffect(() => {
    const executionId = executionReference?.executionId;
    if (!executionId || optimisticExecutionId.current === executionId) return;

    optimisticExecutionId.current = executionId;
    onLockStatusesChange(targetTenantIds.map((TenantId) => ({ TenantId, Locked: false })));
    enqueueSnackbar(t('releaseStageMembers.unlockSubmitted'), { variant: 'success' });
  }, [enqueueSnackbar, onLockStatusesChange, t, executionReference, targetTenantIds]);

  React.useEffect(() => {
    if (!error || reportedError.current === error) return;

    reportedError.current = error;
    enqueueSnackbar(t('releaseStageMembers.unlockFailed'), { variant: 'error' });
  }, [enqueueSnackbar, error, t]);

  const actionDisabled = submitting;

  return (
    <>
      <DefaultButton
        startIcon={<UnlockCircle />}
        disabled={actionDisabled || selectedLockedMembers.length === 0}
        data-testid="release-stage-members-unlock-selected"
        onClick={() => openConfirmation(selectedLockedMembers)}
      >
        {t('releaseStageMembers.actions.unlockSelected')}
      </DefaultButton>
      <ReleaseStageUnlockConfirmationDialog
        id={`release-stage-members-unlock-${releaseStageId}`}
        open={confirmationOpen}
        members={confirmationMembers}
        submitting={submitting}
        onConfirm={confirmUnlock}
        onClose={() => setConfirmationOpen(false)}
      />
    </>
  );
};
